// NOTE: This parameter is used when user is redirected to trip planner to buy additional products
export enum CustomerOriginQueryParamValue {
  // NOTE: This variant is used by shop team to upsell accommodation for users who already bought transport only.
  BookingPage = 'booking-page',
  // NOTE: redirects from the destination search component
  MarketplaceDestinationSearch = 'marketplace-destination-search',
  // NOTE: redirects from hotel search component
  MarketplaceHotelSearch = 'marketplace-hotel-search',
  // NOTE: redirects from flight search component
  MarketplaceFlightSearch = 'marketplace-flight-search',
  // NOTE: redirects from flight list component
  MarketplaceFlightList = 'marketplace-flight-list',
  // NOTE: redirects from flight + hotel search component
  MarketplaceFlightHotelSearch = 'marketplace-flight-hotel-search',
  // NOTE: redirects from multi-stop search component
  MarketplaceMultistopSearch = 'marketplace-multistop-search',
  // NOTE: redirects from the "By car" tab and SideMenu
  MarketplaceCar = 'marketplace-car',
  // NOTE: redirects from Amelia
  MarketplaceAi = 'marketplace-ai',
  // NOTE: redirects from Flyer, Places, and HotelPlaces pages
  MarketplaceFlyer = 'marketplace-flyer',
  // NOTE: redirects from recently viewed components
  MarketplaceRecentlyViewed = 'marketplace-recently-viewed',
  // NOTE: redirects from saved trip components
  MarketplaceSavedTrip = 'marketplace-saved-trip',
  // NOTE: redirects from the Stenaline creator page
  MarketplaceFerryHotelSearch = 'marketplace-ferry-hotel-search',
  // NOTE: redirects from the stenaline-search-component
  StenalineSearch = 'stenaline-search-component',
  // NOTE: used in legacy or uncertain flows (e.g. short URL service)
  MarketplaceGeneric = 'marketplace-generic',
  // NOTE: used on reload modal click for infinite load
  InfiniteLoadReload = 'infinite-load-reload',
  // NOTE: Used when coming from marketplace country page
  MarketplaceCountrySearch = 'marketplace-country-search',
  // NOTE: Used by insight team from retargeting for hotel upsell
  RetargetingHotelUpsell = 'retargeting-hotel-upsell',
  // NOTE: Used by insight team from retargeting emails
  RetargetingHotels = 'retargeting-hotels',
  RetargetingActivities = 'retargeting-activities',
  RetargetingSavedTrips = 'retargeting-saved-trips',
  // NOTE: Used by other teams for meta search, e.g. Skyscanner, Trivago, Icelolly
  MetaPackage = 'meta-package',
  MetaHotel = 'meta-hotel',
  MetaFlight = 'meta-flight',
  // NOTE: redirect from continue button on places pages
  MarketplacePlace = 'marketplace-place',
  // NOTE: redirect from continue button on hotel pages
  MarketplaceHotel = 'marketplace-hotel',
  // NOTE: redirect from pills on place pages
  MarketplacePlacePills = 'marketplace-place-pills',
  // NOTE: redirect from view more hotels
  MarketplaceMoreHotels = 'marketplace-more-hotels',
  // NOTE: redirect from trip flyer pages when clicking search on trip search tab
  MarketplaceTripSearch = 'marketplace-trip-search',
  // NOTE: used when a user arrives via a shortened URL copied from the share component
  CopiedShortenedUrl = 'copied-shortened-url',
  // NOTE: used by book team when doing redirection from failed booking to planner
  PerformBooking = 'perform-booking',
}

export type CustomerOrigin =
  | 'bookingPage'
  | 'marketplaceDestinationSearch'
  | 'marketplaceHotelSearch'
  | 'marketplaceFlightSearch'
  | 'marketplaceFlightList'
  | 'marketplaceFlightHotelSearch'
  | 'marketplaceMultistopSearch'
  | 'marketplaceCar'
  | 'marketplaceAi'
  | 'marketplaceFlyer'
  | 'marketplaceRecentlyViewed'
  | 'marketplaceSavedTrip'
  | 'marketplaceFerryHotelSearch'
  | 'stenalineSearch'
  | 'marketplaceGeneric'
  | 'infiniteLoadReload'
  | 'marketplaceCountrySearch'
  | 'retargetingHotelUpsell'
  | 'retargetingHotels'
  | 'retargetingActivities'
  | 'retargetingSavedTrips'
  | 'metaPackage'
  | 'metaHotel'
  | 'metaFlight'
  | 'marketplacePlace'
  | 'marketplaceHotel'
  | 'marketplacePlacePills'
  | 'marketplaceMoreHotels'
  | 'marketplaceTripSearch'
  | 'copiedShortenedUrl'
  | 'performBooking'
  | 'unattributed';

// NOTE: This record ensures a typecheck error if CustomerOriginQueryParamValue
// is extended without updating this mapping.
const customerOriginQueryParamToCustomerOrigin: Record<
  CustomerOriginQueryParamValue,
  CustomerOrigin
> = {
  [CustomerOriginQueryParamValue.BookingPage]: 'bookingPage',
  [CustomerOriginQueryParamValue.MarketplaceDestinationSearch]:
    'marketplaceDestinationSearch',
  [CustomerOriginQueryParamValue.MarketplaceHotelSearch]:
    'marketplaceHotelSearch',
  [CustomerOriginQueryParamValue.MarketplaceFlightSearch]:
    'marketplaceFlightSearch',
  [CustomerOriginQueryParamValue.MarketplaceFlightList]:
    'marketplaceFlightList',
  [CustomerOriginQueryParamValue.MarketplaceFlightHotelSearch]:
    'marketplaceFlightHotelSearch',
  [CustomerOriginQueryParamValue.MarketplaceMultistopSearch]:
    'marketplaceMultistopSearch',
  [CustomerOriginQueryParamValue.MarketplaceCar]: 'marketplaceCar',
  [CustomerOriginQueryParamValue.MarketplaceAi]: 'marketplaceAi',
  [CustomerOriginQueryParamValue.MarketplaceFlyer]: 'marketplaceFlyer',
  [CustomerOriginQueryParamValue.MarketplaceRecentlyViewed]:
    'marketplaceRecentlyViewed',
  [CustomerOriginQueryParamValue.MarketplaceSavedTrip]: 'marketplaceSavedTrip',
  [CustomerOriginQueryParamValue.MarketplaceFerryHotelSearch]:
    'marketplaceFerryHotelSearch',
  [CustomerOriginQueryParamValue.StenalineSearch]: 'stenalineSearch',
  [CustomerOriginQueryParamValue.MarketplaceGeneric]: 'marketplaceGeneric',
  [CustomerOriginQueryParamValue.InfiniteLoadReload]: 'infiniteLoadReload',
  [CustomerOriginQueryParamValue.MarketplaceCountrySearch]:
    'marketplaceCountrySearch',
  [CustomerOriginQueryParamValue.RetargetingHotelUpsell]:
    'retargetingHotelUpsell',
  [CustomerOriginQueryParamValue.RetargetingHotels]: 'retargetingHotels',
  [CustomerOriginQueryParamValue.RetargetingActivities]:
    'retargetingActivities',
  [CustomerOriginQueryParamValue.RetargetingSavedTrips]:
    'retargetingSavedTrips',
  [CustomerOriginQueryParamValue.MetaPackage]: 'metaPackage',
  [CustomerOriginQueryParamValue.MetaHotel]: 'metaHotel',
  [CustomerOriginQueryParamValue.MetaFlight]: 'metaFlight',

  [CustomerOriginQueryParamValue.MarketplacePlace]: 'marketplacePlace',
  [CustomerOriginQueryParamValue.MarketplaceHotel]: 'marketplaceHotel',
  [CustomerOriginQueryParamValue.MarketplacePlacePills]:
    'marketplacePlacePills',
  [CustomerOriginQueryParamValue.MarketplaceMoreHotels]:
    'marketplaceMoreHotels',
  [CustomerOriginQueryParamValue.MarketplaceTripSearch]:
    'marketplaceTripSearch',
  [CustomerOriginQueryParamValue.CopiedShortenedUrl]: 'copiedShortenedUrl',
  [CustomerOriginQueryParamValue.PerformBooking]: 'performBooking',
};

export function getCustomerOrigin(
  urlSearchParams: URLSearchParams
): CustomerOrigin {
  const customerOrigin = getCustomerOriginQueryParamValue(urlSearchParams);
  if (customerOrigin) {
    return (
      customerOriginQueryParamToCustomerOrigin[
        customerOrigin as CustomerOriginQueryParamValue
      ] ?? 'unattributed'
    );
  }

  return 'unattributed';
}

export function getInvalidCustomerOriginQueryParamValue(
  urlSearchParams: URLSearchParams
): string | undefined {
  const customerOrigin = getCustomerOriginQueryParamValue(urlSearchParams);

  if (
    customerOrigin &&
    !customerOriginQueryParamToCustomerOrigin[
      customerOrigin as CustomerOriginQueryParamValue
    ]
  ) {
    return customerOrigin;
  }

  return undefined;
}

function getCustomerOriginQueryParamValue(
  urlSearchParams: URLSearchParams
): string | undefined {
  return urlSearchParams.get('customer-origin')?.trim() || undefined;
}
